import {Component, DestroyRef, inject, Injector, OnInit} from '@angular/core';
import {
  ButtonComponent,
  CardComponent,
  HeadingComponent,
  IconComponent,
  LabelComponent,
  LinkWithoutHrefDirective,
  LoadingIndicatorService,
  LocalizedDatePipe,
  ResponsivityService,
  SectionComponent,
  TagComponent,
  WaitingDirective
} from '@icz/angular-essentials';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {
  DocumentDetailService,
  EsslApplicationModulesService,
  FindUserByIdPipe,
  PublishContractConsignmentDialogComponent,
  PublishContractConsignmentDialogData,
  PublishContractConsignmentDialogMode,
  SubjectDetailCardComponent,
} from '|shared';
import {
  ContractPublicationState,
  ContractPublicationType,
  EsslApplicationModule,
  InternalNotificationKey,
  InternalNotificationMessageCode,
  SubjectRecordDto
} from '|api/commons';
import {
  ApiContractPublicationService,
  ContractPublicationActivityDto,
  ContractPublicationDto,
  ContractPublicationUpdateDto,
  ContractPublicationVersionDto
} from '|api/sad';
import {ApiSubjectRecordService} from '|api/subject-register';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  Button,
  ButtonCollectionComponent,
  ColumnTemplateDirective,
  extendDefaultTableConfig,
  FilterType,
  IczEmptyDatasource,
  IczInMemoryDatasource,
  IczTableDataSource,
  TableColumnsData,
  TableComponent
} from '@icz/angular-table';
import {DialogService, IczModalService, IczModalSizeClass} from '@icz/angular-modal';
import {DocumentDto} from '|api/document';
import {Router} from '@angular/router';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {enumToOptions} from '@icz/angular-application-core';
import {
  ApplicationRoute,
  createAbsoluteRoute,
  DocumentDetailRoute,
  DocumentsRoute,
  DocumentToastService,
  DocumentToastType,
  WebSocketNotificationsService
} from '|ui-essl-core';
import {ApiContractPublicationOauthService, ContractPublicationConfigDto} from '|api/config-server';

interface ExtendedContractPublicationActivityDto extends ContractPublicationActivityDto {
  contractPublicationVersionId?: Nullable<number>;
  receivedDocumentId?: Nullable<number>;
  contractVersionId?: Nullable<number>;
  receivedDocumentRefNumber?: Nullable<string>;
  responseDate?: Nullable<string>;
  errorMessage?: Nullable<string>;
  operationType?: ContractPublicationType;
  contractVersionUrl?: Nullable<string>;
  state?: ContractPublicationState;
}

@Component({
  selector: 'icz-document-detail-contract-publication',
  templateUrl: './document-detail-contract-publication.component.html',
  styleUrls: ['./document-detail-contract-publication.component.scss'],
  imports: [WaitingDirective, CardComponent, TagComponent, HeadingComponent, LinkWithoutHrefDirective, IconComponent, LabelComponent, SubjectDetailCardComponent, ButtonComponent, ButtonCollectionComponent, NgTemplateOutlet, TableComponent, ColumnTemplateDirective, AsyncPipe, LocalizedDatePipe, FindUserByIdPipe, TranslatePipe, SectionComponent]
})
export class DocumentDetailContractPublicationComponent implements OnInit {
  protected loadingService = inject(LoadingIndicatorService);
  protected responsivityService = inject(ResponsivityService);
  private translateService = inject(TranslateService);
  private apiContractPublicationService = inject(ApiContractPublicationService);
  private documentDetailService = inject(DocumentDetailService);
  private apiSubjectRecordService = inject(ApiSubjectRecordService);
  private destroyRef = inject(DestroyRef);
  private modalService = inject(IczModalService);
  private documentToastService = inject(DocumentToastService);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private wsNotificationService = inject(WebSocketNotificationsService);
  private esslApplicationModulesService = inject(EsslApplicationModulesService);
  private injector = inject(Injector);
  private apiContractPublicationOauthService = inject(ApiContractPublicationOauthService);

  contractSubject!: SubjectRecordDto;
  contractPublication!: ContractPublicationDto;
  latestContractPublicationVersion!: ContractPublicationVersionDto;
  latestPublishedContractPublicationVersion!: Nullable<ContractPublicationVersionDto>;
  ownDocument!: DocumentDto;
  toBePublished = false;
  publicationSettings: Nullable<ContractPublicationConfigDto>;

  activitiesDatasource: IczTableDataSource<ExtendedContractPublicationActivityDto> = new IczEmptyDatasource();

  activitiesColumnData = new TableColumnsData<keyof ExtendedContractPublicationActivityDto>([
    {id: 'state', label: `Stav zpracování`, filterType: FilterType.ENUM, list: enumToOptions('contractPublicationState', ContractPublicationState), allowTranslation: true},
    {id: 'initUserId', label: `Založil`, filterType: FilterType.NONE, disableSort: true},
    {id: 'initDate', label: `Datum odeslání`, filterType: FilterType.DATETIME, disableSort: true},
    {id: 'operationType', label: `Typ operace`, filterType: FilterType.ENUM, list: enumToOptions('contractPublicationType', ContractPublicationType), toggleable: false},
    {id: 'contractVersionId', label: `ID verze smlouvy`, filterType: FilterType.TEXT, disableSort: true, toggleable: false},
    {id: 'receivedDocumentRefNumber', label: `Číslo jednací odpovědi`, filterType: FilterType.TEXT, disableSort: true, toggleable: false},
    {id: 'responseDate', label: `Datum odpovědi`, filterType: FilterType.DATETIME, disableSort: true},
    {id: 'errorMessage', label: `Popis chyby`, filterType: FilterType.TEXT, disableSort: true},
  ]);

  activitiesTableConfig = extendDefaultTableConfig({
    toolbarConfig: {
      showFilter: false,
      showReload: false,
    }
  });

  get contractIdLinkHeaderText() {
    return `ID: ${this.contractPublication?.contractId} `;
  }

  get contractForInfo() {
    if (this.latestPublishedContractPublicationVersion) {
      return this.latestPublishedContractPublicationVersion;
    } else {
      return this.latestContractPublicationVersion;
    }
  }

  get isContractPublicationModuleActive() {
    return this.esslApplicationModulesService.isEsslApplicationModuleActive(EsslApplicationModule.CONTRACT_PUBLICATION);
  }

  ngOnInit() {
    this.documentDetailService.object$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(document => {
      if (document) {
        this.ownDocument = document;
        this.loadingService.doLoading(
          this.apiContractPublicationOauthService.contractPublicationOauthGet(),
          this
        ).subscribe(settings => {
          this.publicationSettings = settings;
          this.loadPublication(this.ownDocument.id);
        });
      }
    });

    this.wsNotificationService.getMessagesListener$([
      InternalNotificationMessageCode.CONTRACT_PUBLICATION_SUCCESS,
      InternalNotificationMessageCode.CONTRACT_PUBLICATION_ERROR,
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => {
      if (message.parameters && message.parameters[0] && message.parameters[0][InternalNotificationKey.ID] && message.parameters[0][InternalNotificationKey.ID] === this.contractPublication.id) {
        this.loadPublication(this.ownDocument.id);
      }
    });
  }

  loadPublication(documentId: number) {
    this.loadingService.doLoading(
      this.apiContractPublicationService.contractPublicationGetContractPublicationByDocumentId({
        documentId
      }),
      this
    ).subscribe(contractPublication => {
      this.contractPublication = contractPublication;
      this.latestContractPublicationVersion = contractPublication.contractPublicationVersions.at(-1)!;
      this.latestPublishedContractPublicationVersion = contractPublication.contractPublicationVersions.findLast(version => version.state === ContractPublicationState.PUBLISHED || version.state === ContractPublicationState.INVALIDATED);
      let subjectToFind;
      if (this.latestPublishedContractPublicationVersion) {
        subjectToFind = this.latestPublishedContractPublicationVersion.contractPublicationSubjects[0];
      } else {
        subjectToFind = this.latestContractPublicationVersion.contractPublicationSubjects[0];
      }
      if (subjectToFind) {
        this.apiSubjectRecordService.subjectRecordGet({subjectId: subjectToFind.subjectId}).subscribe(subjectRecord => {
          this.contractSubject = subjectRecord;
        });
      }
      const activities: ExtendedContractPublicationActivityDto[] = [];
      this.contractPublication.contractPublicationVersions.forEach(version => {
        if (version.circulationActivity) {
          const extendedVersion: ExtendedContractPublicationActivityDto = {...version.circulationActivity};
          extendedVersion.errorMessage = version.errorMessage;
          extendedVersion.receivedDocumentId = version.receivedDocumentId;
          extendedVersion.receivedDocumentRefNumber = version.receivedDocumentRefNumber;
          extendedVersion.contractVersionId = version.contractVersionId;
          extendedVersion.responseDate = version.responseDate;
          extendedVersion.operationType = version.operationType!;
          extendedVersion.contractVersionUrl = version.contractVersionUrl;
          extendedVersion.contractPublicationVersionId = version.id;
          extendedVersion.state = version.state;
          activities.push(extendedVersion);
        }
      });
      this.activitiesDatasource = new IczInMemoryDatasource(() => activities);
      this.toBePublished = this.contractPublication.latestVersionState === ContractPublicationState.TO_HANDOVER;

      this.contractActionButtons = [
        {
          label: 'Modifikovat smlouvu',
          icon: 'edit',
          show: (this.contractPublication.latestVersionState === ContractPublicationState.PUBLISHED || this.contractPublication.latestVersionState === ContractPublicationState.PUBLISHING_ERROR) && this.isContractPublicationModuleActive,
          action: () => this.openContractPublicationDetail(PublishContractConsignmentDialogMode.MODIFY),
        },
        {
          label: this.publicationTagState === ContractPublicationState.TO_HANDOVER ? 'Úpravy' : 'Náhled',
          icon: 'eye',
          show: true,
          action: () => this.openContractPublicationDetail(PublishContractConsignmentDialogMode.SHOW),
        },
        {
          label: this.publicationSettings!.dispatchWhenSaved ? 'Připravit zveřejnění' : 'Zveřejnit',
          icon: 'handover_to_distribution',
          show: this.toBePublished && this.isContractPublicationModuleActive,
          action: () => this.publishContract(),
        },
        {
          label: 'Více',
          icon: 'more',
          show: (this.contractPublication.latestVersionState === ContractPublicationState.PUBLISHED || this.contractPublication.latestVersionState === ContractPublicationState.PUBLISHING_ERROR) && this.isContractPublicationModuleActive,
          submenuItems: [
            {
              label: 'Přidat komponenty',
              show: true,
              action: () => this.openContractPublicationDetail(PublishContractConsignmentDialogMode.COMPONENTS_ADD),
            },
            {
              label: 'Odebrat komponenty',
              show: true,
              action: () => this.openContractPublicationDetail(PublishContractConsignmentDialogMode.COMPONENTS_REMOVE),
            },
            {
              label: 'Znepřístupnění smlouvy',
              show: true,
              action: () => this.invalidateContract(),
            },
          ]
        }];
    });
  }

  contractComponentsText() {
    const prefix = `${this.translateService.instant('Komponenty smlouvy')}: `;
    if (this.latestPublishedContractPublicationVersion) {
      return `${prefix}${this.latestPublishedContractPublicationVersion.digitalComponents.length}`;
    } else if (this.latestContractPublicationVersion) {
      return `${prefix}${this.latestContractPublicationVersion.digitalComponents.length}`;
    } else {
      return '';
    }
  }

  get hasMultipleSubjects() {
    if (this.latestPublishedContractPublicationVersion) {
      return this.latestPublishedContractPublicationVersion.contractPublicationSubjects.length > 1;
    } else if (this.latestContractPublicationVersion) {
      return this.latestContractPublicationVersion.contractPublicationSubjects.length > 1;
    } else {
      return false;
    }
  }

  contractSubjectsText() {
    const prefix = `${this.translateService.instant('Ukázat všechny protistrany')} `;
    if (this.latestPublishedContractPublicationVersion) {
      return `${prefix}(${this.latestPublishedContractPublicationVersion.contractPublicationSubjects.length})`;
    } else if (this.latestContractPublicationVersion) {
      return `${prefix}(${this.latestContractPublicationVersion.contractPublicationSubjects.length})`;
    } else {
      return '';
    }
  }

  get publicationTagColor() {
    let state;
    if (this.latestPublishedContractPublicationVersion) {
      state = this.latestPublishedContractPublicationVersion.state;
    } else {
      state = this.latestContractPublicationVersion.state;
    }
    switch (state) {
      case ContractPublicationState.PUBLISHED:
        return 'green';
      case ContractPublicationState.INVALIDATED:
        return 'red';
      case ContractPublicationState.NOT_PUBLISHED:
        return 'orange';
      case ContractPublicationState.PUBLISHING_ERROR:
        return 'red';
      case ContractPublicationState.TO_HANDOVER:
        return 'blue';
      default:
        return 'blue';
    }
  }

  get publicationTagState() {
    if (this.latestPublishedContractPublicationVersion) {
      return this.latestPublishedContractPublicationVersion.state;
    } else {
      return this.latestContractPublicationVersion.state;
    }
  }

  contractActionButtons: Button[] = [];

  buttonClicked(button: Button) {
    button.action?.(button);
  }

  openContractVersion(row: ExtendedContractPublicationActivityDto) {
    this.openContractPublicationDetail(PublishContractConsignmentDialogMode.SHOW, row.contractPublicationVersionId!);
  }

  openContractExternal(contractUrl?: Nullable<string>) {
    if (contractUrl) {
      window.open(contractUrl, '_blank');
    } else if (this.latestPublishedContractPublicationVersion && this.latestPublishedContractPublicationVersion.contractVersionUrl) {
      window.open(this.latestPublishedContractPublicationVersion.contractVersionUrl, '_blank');
    }
  }

  openReceivedDocument(row: ExtendedContractPublicationActivityDto) {
    this.router.navigateByUrl(createAbsoluteRoute(
      ApplicationRoute.DOCUMENTS,
      DocumentsRoute.DOCUMENT,
      row.receivedDocumentId!,
      DocumentDetailRoute.PREVIEW,
    ));
  }

  openContractPublicationDetail(mode: PublishContractConsignmentDialogMode, contractPublicationVersionId?: number) {
    let contractVersionToOpen: ContractPublicationVersionDto;
    if (contractPublicationVersionId) {
      contractVersionToOpen = this.contractPublication.contractPublicationVersions.find(version => version.id === contractPublicationVersionId)!;
    } else if (this.latestPublishedContractPublicationVersion) {
      contractVersionToOpen = this.latestPublishedContractPublicationVersion;
    } else {
      contractVersionToOpen = this.latestContractPublicationVersion;
    }

    this.modalService.openComponentInModal<boolean, PublishContractConsignmentDialogData>({
      component: PublishContractConsignmentDialogComponent,
      modalOptions: {
        sizeClass: IczModalSizeClass.WH_RESPONSIVE,
        titleTemplate:
          mode === PublishContractConsignmentDialogMode.CREATE || mode === PublishContractConsignmentDialogMode.SHOW ? 'Publikace smlouvy' :
            mode === PublishContractConsignmentDialogMode.COMPONENTS_ADD ? 'Přidání komponent' :
              mode === PublishContractConsignmentDialogMode.COMPONENTS_REMOVE ? 'Odebrání komponent' :
                mode === PublishContractConsignmentDialogMode.MODIFY ? 'Modifikace smlouvy' : 'Smlouva',
        disableAutoMargin: true,
        injector: this.injector,
      },
      data: {
        document: this.ownDocument,
        mode,
        contractPublicationVersion: contractVersionToOpen,
        contractPublicationId: this.contractPublication.id,
        publicationState: this.contractPublication.latestVersionState,
        toBePublished: this.toBePublished
      }
    }).subscribe(result => {
      if (result) {
        this.loadPublication(this.ownDocument.id);
      }
    });
  }

  publishContract() {
    if (this.publicationSettings && this.publicationSettings.dispatchWhenSaved) {
      this.loadingService.doLoading(
        this.apiContractPublicationService.contractPublicationUpdateContractPublication({
            contractPublicationVersionId: this.latestContractPublicationVersion!.id!,
            body: {...this.latestContractPublicationVersion as ContractPublicationUpdateDto, publish: true},
          }),
          this
        ).subscribe(_ => {
          this.loadPublication(this.ownDocument.id);
          this.documentToastService.dispatchSimpleInfoToast(DocumentToastType.CONTRACT_PUBLICATION_PUBLISH_STARTED);
        });
    } else {
      this.loadingService.doLoading(
        this.apiContractPublicationService.contractPublicationPublishContractPublication({contractPublicationId: this.contractPublication.id}),
        this
      ).subscribe(_ => {
        this.loadPublication(this.ownDocument.id);
        this.documentToastService.dispatchSimpleInfoToast(DocumentToastType.CONTRACT_PUBLICATION_PUBLISH_STARTED);
      });
    }
  }

  invalidateContract() {
    this.dialogService.openQuestionDialogWithAnswer({
      title: 'Znepřístupnění smlouvy',
      description: 'Chystáte se znepřístupnit publikovanou smlouvu.',
      question: 'Chcete pokračovat v znepřístupnění smlouvy?',
      leftButtonTitle: 'Znepřístupnit',
      rightButtonTitle: 'Zrušit',
    }).subscribe(result => {
      if (result) {
        this.loadingService.doLoading(
          this.apiContractPublicationService.contractPublicationInvalidateContractPublication({contractPublicationId: this.contractPublication.id}),
          this
        ).subscribe(_ => {
          this.loadPublication(this.ownDocument.id);
          this.documentToastService.dispatchSimpleInfoToast(DocumentToastType.CONTRACT_PUBLICATION_INVALIDATED);
        });
      }
    });
  }

  protected readonly PublishContractConsignmentDialogMode = PublishContractConsignmentDialogMode;
}
